/**
 * `useAuth` — me 쿼리를 구독하여 `authStore` 로 push 하는 브릿지 훅.
 *
 * 호출측 컴포넌트는 이 훅을 한 번만 호출하면 이후 `useAuthStore` selector 로
 * 어디서나 최신 상태를 구독할 수 있다. 뮤테이션(login/logout) 들도 직접
 * `setUser` 를 호출하므로, me 쿼리와 뮤테이션 결과가 일관되게 스토어로 흐른다.
 */

import { useEffect } from 'react';

import { useMeQuery } from '../features/auth/api';
import { useAuthStore, type AuthStatus, type AuthUser } from '../features/auth/store';

export interface UseAuthResult {
  user: AuthUser | null;
  status: AuthStatus;
  isPending: boolean;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  refetch: ReturnType<typeof useMeQuery>['refetch'];
}

export function useAuth(options?: { enabled?: boolean }): UseAuthResult {
  const enabled = options?.enabled ?? true;
  const query = useMeQuery({ enabled });
  const user = useAuthStore((s) => s.user);
  const status = useAuthStore((s) => s.status);
  const setUser = useAuthStore((s) => s.setUser);
  const hasSuccessfulResult = enabled && query.isSuccess;
  const resolvedUser = hasSuccessfulResult ? query.data ?? null : user;
  const resolvedStatus: AuthStatus = hasSuccessfulResult
    ? query.data
      ? 'authenticated'
      : 'unauthenticated'
    : status;

  useEffect(() => {
    if (!enabled || !query.isSuccess) return;
    // 성공한 null 응답만 명시적인 로그아웃으로 반영한다. 요청 오류는 기존
    // authenticated 상태를 입증하거나 부정하지 않으므로 store 를 보존한다.
    setUser(query.data ?? null);
  }, [enabled, query.isSuccess, query.data, setUser]);

  return {
    user: resolvedUser,
    status: resolvedStatus,
    isPending: query.isPending,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}
